import { useLayoutEffect, useRef, useState } from 'react';
import type { StoryId } from '../story';

type PhoneStageProps = {
  active: StoryId;
  compact?: boolean;
};

function ScreenHeader({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <header className="screen-header">
      <span>{eyebrow}</span>
      <strong>{title}</strong>
      <span className="screen-more" aria-hidden="true">
        ···
      </span>
    </header>
  );
}

function AbstractShow({ variant = 'a' }: { variant?: 'a' | 'b' | 'c' }) {
  return (
    <div className={`abstract-show abstract-show-${variant}`} aria-hidden="true">
      <i />
      <i />
      <i />
      <span />
    </div>
  );
}

function FeedScreen() {
  return (
    <div className="phone-screen feed-screen">
      <ScreenHeader eyebrow="Friends+" title="Feed" />
      <div className="scope-chips" aria-label="Illustrative feed audiences">
        <span className="is-active">Friends+</span>
        <span>Friends</span>
        <span>Public</span>
      </div>
      <div className="feed-grid">
        <article className="memory-tile memory-tile-tall">
          <AbstractShow />
          <div className="tile-score">9.4</div>
          <div className="tile-copy">
            <b>Tidal Circuit</b>
            <span>Lantern Hall · last night</span>
          </div>
        </article>
        <article className="memory-tile">
          <div className="ticket-stock">
            <span>MOONLIT MARKET</span>
            <b>8.8</b>
          </div>
          <div className="tile-copy">
            <b>Moonlit Market</b>
            <span>Signal Grounds · 4d</span>
          </div>
        </article>
        <article className="memory-tile">
          <AbstractShow variant="b" />
          <div className="tile-copy">
            <b>Glass Harbor</b>
            <span>Shared by Theo · 1w</span>
          </div>
        </article>
      </div>
    </div>
  );
}

export function LogScreen() {
  const [revealed, setRevealed] = useState(false);
  const revealButtonRef = useRef<HTMLButtonElement>(null);
  const resetButtonRef = useRef<HTMLButtonElement>(null);
  const pendingFocus = useRef<'reveal' | 'reset' | null>(null);

  useLayoutEffect(() => {
    if (pendingFocus.current === 'reveal') {
      resetButtonRef.current?.focus({ preventScroll: true });
    } else if (pendingFocus.current === 'reset') {
      revealButtonRef.current?.focus({ preventScroll: true });
    }
    pendingFocus.current = null;
  }, [revealed]);

  const revealScore = () => {
    pendingFocus.current = 'reveal';
    setRevealed(true);
  };

  const resetComparison = () => {
    pendingFocus.current = 'reset';
    setRevealed(false);
  };

  return (
    <div className={`phone-screen log-screen ${revealed ? 'is-revealed' : ''}`}>
      <ScreenHeader eyebrow={revealed ? 'Your night' : 'Compare · 3 of 5'} title={revealed ? 'Logged' : 'Which night wins?'} />
      {revealed ? (
        <div className="reveal-card" aria-live="polite">
          <div className="reveal-light" aria-hidden="true" />
          <span className="reveal-label">GLASS HARBOR AFTERDARK</span>
          <strong className="reveal-score">9.4</strong>
          <span className="rank-stamp">#3 OF 47</span>
          <div className="reveal-meta">
            <span>Lantern Hall</span>
            <span>Jul 11 · Sec 112</span>
          </div>
          <button
            className="phone-action phone-action-ghost"
            type="button"
            ref={resetButtonRef}
            onClick={resetComparison}
          >
            Compare again
          </button>
        </div>
      ) : (
        <div className="duel">
          <article className="duel-card duel-card-primary">
            <AbstractShow variant="c" />
            <span>TONIGHT</span>
            <b>Tidal Circuit</b>
            <small>Lantern Hall</small>
          </article>
          <span className="duel-or">OR</span>
          <article className="duel-card">
            <div className="ticket-stock ticket-stock-small">
              <b>8.9</b>
            </div>
            <span>LAST SUMMER</span>
            <b>Moonlit Market</b>
            <small>Signal Grounds</small>
          </article>
          <button className="phone-action" type="button" ref={revealButtonRef} onClick={revealScore}>
            Tonight wins — reveal score
          </button>
        </div>
      )}
    </div>
  );
}

function TimelineView() {
  return (
    <div className="timeline-wheel">
      <span className="month-label">JUL 2026</span>
      <article className="timeline-card timeline-card-muted">
        <span>Moonlit Market Sessions</span>
        <b>8.8</b>
      </article>
      <div className="today-divider" aria-label="Today">
        <span>TODAY</span>
      </div>
      <article className="timeline-card timeline-card-current">
        <AbstractShow variant="a" />
        <div>
          <span>Glass Harbor Afterdark</span>
          <b>9.4</b>
          <small>Lantern Hall · Baltimore</small>
        </div>
      </article>
      <article className="timeline-card timeline-card-muted">
        <span>Tidal Circuit</span>
        <b>9.1</b>
      </article>
    </div>
  );
}

function MapView() {
  return (
    <div className="collection-map" aria-label="Illustrative collection map">
      <svg viewBox="0 0 260 270" aria-hidden="true">
        <path d="M34 30c27 18 49 8 71 23 19 13 13 39 37 48 23 8 39-6 61 9 32 22 15 52 30 77 9 15 23 26 7 47-18 25-55 7-74 9-35 3-53 29-84 10-20-12-14-38-28-54-12-32-44-50-30-83 13-30-6-55 10-76z" />
        <path d="M30 146c50-12 83 17 122 3 36-13 57-41 87-35M83 43c11 39-14 60-1 95 14 37 44 61 33 111" />
      </svg>
      <span className="map-pin map-pin-a" aria-label="Lantern Hall, three shows">
        3
      </span>
      <span className="map-pin map-pin-b" aria-label="Signal Grounds, two shows">
        2
      </span>
      <span className="map-pin map-pin-c" aria-label="Northline Room, one show">
        1
      </span>
      <div className="map-summary">
        <span>YOUR LIVE MAP</span>
        <b>8 venues · 4 cities</b>
      </div>
    </div>
  );
}

export function RememberScreen() {
  const [mode, setMode] = useState<'timeline' | 'map'>('timeline');

  return (
    <div className="phone-screen remember-screen">
      <ScreenHeader eyebrow="Your history" title={mode === 'timeline' ? 'Timeline' : 'Map'} />
      <div className="screen-toggle" role="group" aria-label="Memory view">
        <button
          type="button"
          className={mode === 'timeline' ? 'is-active' : ''}
          aria-pressed={mode === 'timeline'}
          onClick={() => setMode('timeline')}
        >
          Timeline
        </button>
        <button
          type="button"
          className={mode === 'map' ? 'is-active' : ''}
          aria-pressed={mode === 'map'}
          onClick={() => setMode('map')}
        >
          Map
        </button>
      </div>
      {mode === 'timeline' ? <TimelineView /> : <MapView />}
    </div>
  );
}

function ConnectScreen() {
  return (
    <div className="phone-screen connect-screen">
      <ScreenHeader eyebrow="Artist" title="Tidal Circuit" />
      <div className="artist-hero">
        <AbstractShow variant="b" />
        <div className="artist-title">
          <span>YOU × TIDAL CIRCUIT</span>
          <strong>4 nights</strong>
          <small>First seen · 2023</small>
        </div>
      </div>
      <section className="friend-overlap">
        <div className="phone-section-title">
          <span>FRIENDS WHO SAW THEM</span>
          <b>6</b>
        </div>
        <div className="avatar-row" aria-label="Illustrative profiles">
          {['MR', 'DO', 'JC', 'TP'].map((initials, index) => (
            <span key={initials} style={{ '--avatar-index': index } as React.CSSProperties}>
              {initials}
            </span>
          ))}
        </div>
      </section>
      <article className="overlap-card">
        <span>SHARED HISTORY</span>
        <b>You and Mia were at 3 of the same shows.</b>
        <span className="phone-text-action">See the nights →</span>
      </article>
    </div>
  );
}

function PlanScreen() {
  const [tab, setTab] = useState<'shows' | 'tickets' | 'alerts'>('shows');
  const [reminders, setReminders] = useState(true);

  return (
    <div className="phone-screen plan-screen">
      <ScreenHeader eyebrow="Baltimore · 25 mi" title="Plan" />
      <label className="search-shell">
        <span className="sr-only">Search artists, venues, and shows</span>
        <span aria-hidden="true">⌕</span>
        <input type="search" placeholder="Artists, venues, shows" />
      </label>
      <div className="plan-tabs" aria-label="Plan sections">
        {(['shows', 'tickets', 'alerts'] as const).map((option) => (
          <button
            className={tab === option ? 'is-active' : ''}
            type="button"
            key={option}
            aria-pressed={tab === option}
            onClick={() => setTab(option)}
          >
            {option[0].toUpperCase() + option.slice(1)}
          </button>
        ))}
      </div>
      {tab === 'shows' ? (
        <div className="plan-panel">
          <article className="upcoming-show">
            <div className="countdown-stamp"><b>07</b><span>DAYS</span></div>
            <div><span>RECOMMENDED · 12 MI</span><b>Moonlit Market Sessions</b><small>Signal Grounds · Sat, Aug 22</small></div>
          </article>
          <article className="discovery-mini"><span>VENUE</span><b>Lantern Hall</b><small>8 upcoming shows · 14 seat views</small></article>
        </div>
      ) : null}
      {tab === 'tickets' ? (
        <div className="plan-panel">
          <article className="wallet-stub">
            <div><span>UP NEXT</span><b>Tidal Circuit</b><small>Lantern Hall · Sec 112 · Row F</small></div>
            <span className="barcode" aria-label="Illustrative ticket barcode" />
          </article>
          <p className="phone-note">Keep ticket details together. Add manually or scan a barcode.</p>
        </div>
      ) : null}
      {tab === 'alerts' ? (
        <div className="plan-panel alerts-panel">
          <div className="alert-row"><span>SHOW REMINDER</span><button type="button" aria-pressed={reminders} onClick={() => setReminders((value) => !value)}>{reminders ? 'On' : 'Off'}</button></div>
          <div className="alert-row"><span>TICKETS ON SALE</span><b>On</b></div>
          <div className="alert-row"><span>POST-SHOW PROMPT</span><b>On</b></div>
        </div>
      ) : null}
    </div>
  );
}

const screens: Record<StoryId, () => React.JSX.Element> = {
  feed: FeedScreen,
  log: LogScreen,
  remember: RememberScreen,
  connect: ConnectScreen,
  plan: PlanScreen,
};

export function PhoneStage({ active, compact = false }: PhoneStageProps) {
  const Screen = screens[active];
  return (
    <div className={`phone-stage ${compact ? 'phone-stage-compact' : ''}`}>
      <p className="preview-label">Illustrative scenarios; names/data do not represent actual Sticket users or attendance.</p>
      <div className="phone-shell" data-scene={active}>
        <div className="phone-status" aria-hidden="true">
          <span>9:41</span><i /><span>● ◒</span>
        </div>
        <div className="phone-viewport">
          <Screen />
        </div>
        <div className="phone-home" aria-hidden="true" />
      </div>
    </div>
  );
}
